import { useState, useEffect } from "react";
import { Plus, Pencil, Power, Upload, History, Download, Trash2, FilterX } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/common/PageHeader";
import { UploadLimitBanner } from "../components/common/UploadLimitBanner";
import { DataTable } from "../components/common/DataTable";
import GeoMasterPanel from "./GeoMasterPanel";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";

const FIELDS = [["consumer_number", "Consumer Number"], ["name", "Name"], ["mobile", "Mobile"], ["address", "Address"], ["division", "Division"], ["section", "Section"], ["area", "Sub Division"], ["discom", "DISCOM"], ["old_meter_no", "Old Meter No"], ["meter_kw", "Meter KW"]];

export default function ConsumerMaster() {
  const { has } = useAuth();
  const canManage = has("CONSUMER.MANAGE");
  const canImport = has("CONSUMER.IMPORT");
  const canBulkDelete = has("CONSUMER.BULK_DELETE");
  const [refresh, setRefresh] = useState(0);
  const [tab, setTab] = useState("consumers");
  const [clearNonce, setClearNonce] = useState(0);
  const [open, setOpen] = useState(false); const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({}); const [saving, setSaving] = useState(false);
  const [impOpen, setImpOpen] = useState(false); const [preview, setPreview] = useState(null); const [busy, setBusy] = useState(false);
  const [delOpen, setDelOpen] = useState(false); const [delPreview, setDelPreview] = useState(null); const [delBusy, setDelBusy] = useState(false);
  const [totalCount, setTotalCount] = useState(null);

  useEffect(() => {
    api.get("/consumers", { params: { page: 1, page_size: 1 } })
      .then((r) => setTotalCount(r.data.total)).catch(() => {});
  }, [refresh]);

  const openCreate = () => { setEditing(null); setForm({}); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...r }); setOpen(true); };
  const save = async () => {
    if (!form.consumer_number?.trim()) { toast.error("Consumer Number required"); return; }
    if (saving) return; setSaving(true);
    try {
      if (editing) { const { consumer_number, id, ...rest } = form; await api.put(`/consumers/${editing.id}`, rest); }
      else await api.post("/consumers", form);
      toast.success("Consumer saved"); setOpen(false); setRefresh((n) => n + 1);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };
  const toggle = async (r) => { try { await api.patch(`/consumers/${r.id}/status?is_active=${!r.is_active}`); setRefresh((n) => n + 1); } catch { toast.error("Failed"); } };

  const errMsg = (err, fallback) => {
    const d = err?.response?.data?.detail;
    if (typeof d === "string" && d) return d;
    if (Array.isArray(d) && d.length) return d.map((x) => x?.msg || String(x)).join("; ");
    if (err?.response?.status) return `${fallback}: server returned ${err.response.status}`;
    return err?.message || fallback;
  };
  const [chunkProg, setChunkProg] = useState(null);
  const doPreview = async (e) => {
    const f = e.target.files?.[0]; if (!f) return; setBusy(true); setPreview(null); setChunkProg(null);
    try {
      let data;
      if (f.size > 800 * 1024) {
        // chunked upload — stays under the proxy body-size limit (avoids 413 on big files)
        const CHUNK = 512 * 1024;
        const uid = (await api.post("/consumers/import/preview/init")).data.upload_id;
        const total = Math.ceil(f.size / CHUNK);
        for (let i = 0; i < total; i++) {
          setChunkProg({ i: i + 1, total });
          await api.put("/consumers/import/preview/chunk", f.slice(i * CHUNK, (i + 1) * CHUNK),
            { params: { upload_id: uid, index: i }, headers: { "Content-Type": "application/octet-stream" } });
        }
        data = (await api.post("/consumers/import/preview/finish", null, { params: { upload_id: uid } })).data;
      } else {
        const fd = new FormData(); fd.append("file", f);
        data = (await api.post("/consumers/import/preview", fd)).data;
      }
      if (data.already || data.status === "applied") {
        toast.info("This exact file was already imported earlier. No changes to preview.");
        setImpOpen(false); setRefresh((n) => n + 1);
      } else {
        setPreview(data);
      }
    }
    catch (err) { toast.error(errMsg(err, "Preview failed")); } finally { setBusy(false); setChunkProg(null); e.target.value = ""; }
  };
  const downloadTemplate = async () => {
    try {
      const res = await api.get("/consumers/import/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "consumer_upload_template.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Template download failed"); }
  };
  const doApply = async () => {
    if (!preview?.batch_id || busy) return; setBusy(true);
    const fd = new FormData(); fd.append("batch_id", preview.batch_id);
    try { const { data } = await api.post("/consumers/import/apply", fd); toast.success(`Imported: +${data.summary.inserted} new, ${data.summary.updated} updated, ${data.summary.skipped} skipped`); setImpOpen(false); setPreview(null); setRefresh((n) => n + 1); }
    catch (e) { toast.error(errMsg(e, "Apply failed")); } finally { setBusy(false); }
  };
  const rejectedCount = (preview?.summary?.INVALID_CONSUMER_NUMBER || 0) + (preview?.summary?.DUPLICATE_IN_FILE || 0);
  const downloadRejected = async () => {
    try { await blobDownload(`/consumers/import/${preview.batch_id}/rejected`, "consumer_import_errors.xlsx"); }
    catch (e) { toast.error(errMsg(e, "Download failed")); }
  };

  const blobDownload = async (url, filename) => {
    const res = await api.get(url, { responseType: "blob" });
    const u = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = u; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(u);
  };
  const delTemplate = async () => {
    try { await blobDownload("/consumers/bulk-delete/template", "consumer_bulk_delete_template.xlsx"); }
    catch { toast.error("Template download failed"); }
  };
  const delDoPreview = async (e) => {
    const f = e.target.files?.[0]; if (!f) return; setDelBusy(true); setDelPreview(null);
    const fd = new FormData(); fd.append("file", f);
    try { const { data } = await api.post("/consumers/bulk-delete/preview", fd); setDelPreview(data); }
    catch (err) { toast.error(err.response?.data?.detail || "Preview failed"); } finally { setDelBusy(false); e.target.value = ""; }
  };
  const delDoApply = async () => {
    if (!delPreview?.batch_id || delBusy) return;
    if (!window.confirm(`Delete ${delPreview.summary.effective_delete} consumer(s)? This cannot be undone.`)) return;
    setDelBusy(true);
    const fd = new FormData(); fd.append("batch_id", delPreview.batch_id);
    try { const { data } = await api.post("/consumers/bulk-delete/apply", fd); toast.success(`Deleted ${data.summary.deleted} consumer(s)`); setDelOpen(false); setDelPreview(null); setRefresh((n) => n + 1); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); } finally { setDelBusy(false); }
  };
  const delDownloadUnmatched = async () => {
    try { await blobDownload(`/consumers/bulk-delete/${delPreview.batch_id}/unmatched`, "consumer_delete_unmatched.xlsx"); }
    catch { toast.error("Download failed"); }
  };

  const columns = [
    { key: "consumer_number", label: "Consumer #", mono: true },
    { key: "name", label: "Name", render: (r) => <span className="font-medium text-slate-900">{r.name || "—"}</span> },
    { key: "mobile", label: "Mobile" },
    { key: "division", label: "Div/Sec", render: (r) => `${r.division || "—"}/${r.section || "—"}` },
    { key: "discom", label: "DISCOM" },
    { key: "status", label: "Status", render: (r) => <Badge className={r.is_active ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-slate-200 text-slate-600"}>{r.is_active ? "Active" : "Inactive"}</Badge> },
  ];

  return (
    <div>
      <PageHeader title="Consumer Master" subtitle="Global consumer registry (normalized consumer number)" />
      <div className="mb-4 flex gap-2" data-testid="consumer-tabs">
        {[["consumers", "Consumers"], ["division", "Division Master"], ["section", "Section Master"], ["subdivision", "Sub Division Master"]].map(([k, l]) => (
          <button key={k} data-testid={`consumer-tab-${k}`} onClick={() => setTab(k)}
            className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${tab === k ? "bg-[var(--mf-purple-600)] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{l}</button>
        ))}
      </div>
      {tab === "division" && <GeoMasterPanel kind="division" label="Division" />}
      {tab === "section" && <GeoMasterPanel kind="section" label="Section" />}
      {tab === "subdivision" && <GeoMasterPanel kind="subdivision" label="Sub Division" />}
      {tab === "consumers" && (<>
      <UploadLimitBanner kind="consumer" />
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <Badge data-testid="consumer-total-count" className="bg-teal-50 text-teal-700 hover:bg-teal-50 text-sm">
          Total Consumers: {totalCount === null ? "…" : totalCount.toLocaleString("en-IN")}
        </Badge>
        <div className="flex gap-2">
          {canImport && <Button data-testid="consumer-import-btn" variant="outline" size="sm" onClick={() => { setPreview(null); setImpOpen(true); }}><Upload className="mr-1.5 h-4 w-4" /> Import Excel</Button>}
          {canBulkDelete && <Button data-testid="consumer-bulk-delete-btn" variant="outline" size="sm" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => { setDelPreview(null); setDelOpen(true); }}><Trash2 className="mr-1.5 h-4 w-4" /> Bulk Delete</Button>}
          {canManage && <Button data-testid="consumer-add" size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={openCreate}><Plus className="mr-1.5 h-4 w-4" /> New Consumer</Button>}
        </div>
      </div>
      <DataTable key={`ct-${clearNonce}`} testid="consumer-table" columns={columns} searchPlaceholder="Search consumer no, name, old meter no, mobile..." refreshKey={refresh}
        toolbar={<Button variant="outline" size="sm" data-testid="consumer-clear-filters" onClick={() => setClearNonce((n) => n + 1)}><FilterX className="mr-1.5 h-4 w-4" /> Clear</Button>}
        load={(params) => api.get("/consumers", { params }).then((r) => r.data)} emptyText="No consumers yet"
        rowActions={canManage ? (r) => (
          <div className="flex justify-end gap-1">
            <Button data-testid={`consumer-edit-${r.id}`} variant="ghost" size="sm" onClick={() => openEdit(r)}><Pencil className="h-4 w-4" /></Button>
            <Button data-testid={`consumer-toggle-${r.id}`} variant="ghost" size="sm" onClick={() => toggle(r)}><Power className={`h-4 w-4 ${r.is_active ? "text-red-500" : "text-emerald-500"}`} /></Button>
          </div>
        ) : null} />

      <Dialog open={open} onOpenChange={(o) => !saving && setOpen(o)}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Consumer" : "New Consumer"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {FIELDS.map(([k, l]) => (
              <div key={k} className={k === "address" ? "col-span-2" : ""}>
                <Label className="text-xs">{l}{k === "consumer_number" ? " *" : ""}</Label>
                <Input data-testid={`consumer-field-${k}`} disabled={editing && k === "consumer_number"} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1" />
              </div>
            ))}
          </div>
          <DialogFooter><Button variant="outline" disabled={saving} onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="consumer-save" disabled={saving} onClick={save} className="bg-blue-600 hover:bg-blue-700">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={impOpen} onOpenChange={(o) => !busy && setImpOpen(o)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Import Consumers</DialogTitle></DialogHeader>
          {!preview && <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center">
            <div className="mb-3">
              <Button data-testid="consumer-template-btn" variant="outline" size="sm" onClick={downloadTemplate}>
                <Download className="mr-1.5 h-4 w-4" /> Download Sample Template
              </Button>
            </div>
            <input id="cimp" type="file" accept=".xlsx" className="hidden" onChange={doPreview} data-testid="consumer-import-file" />
            <label htmlFor="cimp" className="cursor-pointer text-sm text-blue-600">{busy ? (chunkProg ? `Uploading part ${chunkProg.i}/${chunkProg.total}…` : "Reading…") : "Choose an .xlsx file (Preview only)"}</label>
          </div>}
          {preview && <div>
            <div className="mb-3 flex flex-wrap items-center gap-2 text-xs" data-testid="consumer-preview-summary">
              {Object.entries(preview.summary || {}).map(([k, v]) => <Badge key={k} variant="outline">{k}: {v}</Badge>)}
              <Badge className="bg-slate-100 text-slate-700">Total rows: {preview.total_rows ?? "—"}</Badge>
              {rejectedCount > 0 && (
                <Button data-testid="consumer-rejected-btn" variant="outline" size="sm" className="h-7 border-red-200 text-red-600" onClick={downloadRejected}>
                  <Download className="mr-1 h-3.5 w-3.5" /> Download Errors ({rejectedCount})
                </Button>
              )}
            </div>
            <div className="max-h-64 overflow-y-auto rounded-lg border text-xs">
              <table className="w-full"><thead className="sticky top-0 bg-slate-50"><tr><th className="p-2 text-left">Row</th><th className="p-2 text-left">Consumer #</th><th className="p-2 text-left">Class</th><th className="p-2 text-left">Reason</th></tr></thead>
                <tbody>{(preview.preview || []).map((p) => <tr key={p.row} className="border-t"><td className="p-2">{p.row}</td><td className="p-2 font-mono">{p.consumer_number || "—"}</td><td className="p-2">{p.classification}</td><td className="p-2 text-red-600">{p.classification === "INVALID_CONSUMER_NUMBER" ? "Consumer Number missing or invalid" : p.classification === "DUPLICATE_IN_FILE" ? "Duplicate Consumer Number within the file" : ""}</td></tr>)}</tbody></table>
            </div>
          </div>}
          <DialogFooter>
            <Button variant="outline" disabled={busy} onClick={() => { setPreview(null); setImpOpen(false); }}>Cancel</Button>
            {preview && <Button data-testid="consumer-apply-btn" disabled={busy} onClick={doApply} className="bg-emerald-600 hover:bg-emerald-700">Apply Import</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={delOpen} onOpenChange={(o) => !delBusy && setDelOpen(o)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Bulk Delete Consumers</DialogTitle></DialogHeader>
          {!delPreview && <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center">
            <div className="mb-3">
              <Button data-testid="consumer-delete-template-btn" variant="outline" size="sm" onClick={delTemplate}>
                <Download className="mr-1.5 h-4 w-4" /> Download Template
              </Button>
              <p className="mt-2 text-xs text-slate-500">Columns: <b>Consumer Number</b>, <b>Delete Reason</b></p>
            </div>
            <input id="cdel" type="file" accept=".xlsx" className="hidden" onChange={delDoPreview} data-testid="consumer-delete-file" />
            <label htmlFor="cdel" className="cursor-pointer text-sm text-red-600">{delBusy ? "Reading…" : "Choose an .xlsx file (Preview only)"}</label>
          </div>}
          {delPreview && <div>
            <div className="mb-3 grid grid-cols-2 gap-2 text-center sm:grid-cols-4" data-testid="consumer-delete-summary">
              <div className="rounded-lg border p-2"><div className="text-lg font-semibold text-slate-800">{delPreview.summary.total}</div><div className="text-xs text-slate-500">Total Rows</div></div>
              <div className="rounded-lg border p-2"><div className="text-lg font-semibold text-emerald-600">{delPreview.summary.matched}</div><div className="text-xs text-slate-500">Matched</div></div>
              <div className="rounded-lg border p-2"><div className="text-lg font-semibold text-amber-600">{delPreview.summary.unmatched}</div><div className="text-xs text-slate-500">Unmatched</div></div>
              <div className="rounded-lg border border-red-200 bg-red-50 p-2"><div className="text-lg font-semibold text-red-600">{delPreview.summary.effective_delete}</div><div className="text-xs text-slate-500">To Delete</div></div>
            </div>
            {delPreview.summary.unmatched > 0 && (
              <Button data-testid="consumer-delete-unmatched-btn" variant="outline" size="sm" className="mb-3" onClick={delDownloadUnmatched}>
                <Download className="mr-1.5 h-4 w-4" /> Download Unmatched ({delPreview.summary.unmatched})
              </Button>
            )}
            <div className="max-h-56 overflow-y-auto rounded-lg border text-xs">
              <table className="w-full"><thead className="sticky top-0 bg-slate-50"><tr><th className="p-2 text-left">Row</th><th className="p-2 text-left">Consumer #</th><th className="p-2 text-left">Name</th><th className="p-2 text-left">Reason</th></tr></thead>
                <tbody>{(delPreview.preview || []).map((p) => <tr key={p.row} className="border-t"><td className="p-2">{p.row}</td><td className="p-2 font-mono">{p.consumer_number}</td><td className="p-2">{p.name || "—"}</td><td className="p-2">{p.delete_reason || "—"}</td></tr>)}</tbody></table>
            </div>
          </div>}
          <DialogFooter>
            <Button variant="outline" disabled={delBusy} onClick={() => { setDelPreview(null); setDelOpen(false); }}>Cancel</Button>
            {delPreview && delPreview.summary.effective_delete > 0 && <Button data-testid="consumer-delete-apply-btn" disabled={delBusy} onClick={delDoApply} className="bg-red-600 hover:bg-red-700">Confirm Delete ({delPreview.summary.effective_delete})</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </>)}
    </div>
  );
}
